// DEPENDENCIES ---------------------------------------------------------------- //

import { reportRooftopLoadFailed } from '@features/auth/services/profileReport';
import React from 'react';
import { useLazyQuery } from '@apollo/client';
import { GetHeaderRooftopsDocument, GetLinkedHeaderRooftopsDocument } from '@hopdrive/feature-move-planner';

import { useUser, setRooftop, setCustomerConfig, setCustomerConfigLoaded, useRooftop, setCustomerTierMap, setRooftops as setStoreRooftops } from '@store';
import { applyRooftopSwitch } from '../utils/switchRooftop';
import { checkForPowerRole, getClaimsFromToken } from '@features/auth/services/authService';
import { Customers } from '@gql/base-types';

import { css } from '@emotion/css';
import { Theme, useTheme, Autocomplete, InputAdornment, TextField, Tooltip } from '@mui/material';

import { BiStore } from 'react-icons/bi';

// DEFAULTS ---------------------------------------------------------------- //

const getDefaultRooftopId = (rooftopId?: number) => {
  const localRooftopId = parseInt(localStorage?.getItem(`rooftopId`) || `0`);
  return localRooftopId || rooftopId || 0;
};

// COMPONENT ---------------------------------------------------------------- //

const HeaderRooftopSelect = () => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const user = useUser();
  const currentRooftop = useRooftop();
  const hasAdminPrivileges = checkForPowerRole(user);

  const [getHeaderRooftops] = useLazyQuery(GetHeaderRooftopsDocument);
  const [getLinkedHeaderRooftops] = useLazyQuery(GetLinkedHeaderRooftopsDocument);

  const [rooftops, setRooftops] = React.useState<Customers[]>([]);
  const [rooftopId, setRooftopId] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState(true);

  const [errorText, setErrorText] = React.useState<string>(``);

  // Sync local rooftopId with store rooftop
  React.useEffect(() => {
    if (currentRooftop) {
      setRooftopId(currentRooftop.id);
    }
  }, [currentRooftop]);

  React.useEffect(() => {
    const getUserClaims = async () => {
      const claims = await getClaimsFromToken();
      return claims;
    };
    // An admin sees every rooftop. Every other role sees the rooftops its user is linked to
    // (usertocustomers), never the token's allowed customers: the token holds only so many ids,
    // and a program admin can be linked to hundreds of rooftops.
    const fetchRooftopsBasedOnRole = async (userRole: string, userId?: number) => {
      if (!userRole) throw new Error(`User role not found`);

      if (userRole !== 'admin' && userRole !== 'dealer-admin' && userRole !== 'dealer-super-admin' && userRole !== 'program-admin')
        throw new Error(`User role not allowed`);
      if (userRole === 'admin') return getHeaderRooftops();
      if (!userId) throw new Error(`User id not found`);
      return getLinkedHeaderRooftops({ variables: { userId } });
    };
    const fetchRooftopsAndSetState = async () => {
      try {
        setIsLoading(true);

        const userClaims = await getUserClaims();
        if (!userClaims) throw new Error(`User claims not found`);

        const userRole = userClaims['x-hasura-default-role'];
        if (!userRole) throw new Error(`User role not found`);

        const userId = userClaims['x-hasura-user-id'] ?? user?.profile?.id ?? undefined;
        const res = await fetchRooftopsBasedOnRole(userRole, userId);
        const resRooftops = res?.data?.customers || [];

        const defaultCustomerId =
          typeof userClaims['x-hasura-customer-id'] === 'number'
            ? userClaims['x-hasura-customer-id']
            : userRole === 'admin'
              ? 9
              : 0;
        // The remembered rooftop, else the token's customer. When neither is in the user's list
        // (a home customer the user isn't linked to, or a token minted elsewhere), the first
        // rooftop in the list, so the portal never opens on no rooftop.
        const preferredRooftopId = getDefaultRooftopId(defaultCustomerId);
        const defaultRooftop = resRooftops?.find(r => r?.id === preferredRooftopId) ?? resRooftops?.[0];
        const defaultRooftopId = defaultRooftop?.id ?? preferredRooftopId;

        setRooftops(resRooftops as Customers[]);
        // Mirror to the store — the list is the access check for programmatic
        // switches (search's switch-on-click).
        setStoreRooftops(resRooftops as Customers[]);
        setRooftopId(defaultRooftopId);
        setRooftop(defaultRooftop as Customers);

        // Build customer tier map for tier-aware feature permissions. Cross-rooftop `customers.tier` read (named exception): the product-access snapshot covers only the working rooftop, and this decision spans every rooftop the user is linked to.
        const tierMap: Record<number, string> = {};
        for (const r of resRooftops) {
          if (r?.id && r?.tier) tierMap[r.id] = r.tier;
        }
        setCustomerTierMap(tierMap);

        setErrorText(``);

        if (!defaultRooftop) {
          // No rooftop to open on: the portal will have no customer and no products.
          reportRooftopLoadFailed({
            source: 'header_rooftops',
            outcome: 'empty',
            operation: userRole === 'admin' ? 'GetHeaderRooftops' : 'GetLinkedHeaderRooftops',
            variables: userRole === 'admin' ? {} : { userId },
            role: userRole,
            userId: typeof userId === 'number' ? userId : null,
          });
          setIsLoading(false);
          return;
        }
        // effective_config is the DB-computed merge of default + organization + customer config
        // (what enrichCustomerConfig used to assemble per request), so read it straight off the
        // rooftop record instead of a round-trip to the enrich function.
        if (defaultRooftop?.effective_config) {
          setCustomerConfig(defaultRooftop.effective_config);
        }

        setIsLoading(false);
      } catch (err) {
        console.error(`HeaderRooftopSelect: Failed to fetch global markets:`, err);
        reportRooftopLoadFailed({
          source: 'header_rooftops',
          outcome: 'request_failed',
          operation: 'GetHeaderRooftops / GetLinkedHeaderRooftops',
          variables: { userId: user?.profile?.id ?? null },
          role: user?.profile?.default_role,
          userId: user?.profile?.id,
          error: err,
        });
        setErrorText(`Failed to fetch global markets`);
        setIsLoading(false);
      } finally {
        // Power-role users (admin / dealer-super-admin / program-admin) load their customerConfig
        // here, not through Layout's fetchAndSetDefaultRooftop — so mark config resolution settled
        // once this completes, or config-gated routes would flash NoMatch. See customerConfigLoaded.
        setCustomerConfigLoaded(true);
      }
    };
    fetchRooftopsAndSetState();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (errorText) console.error(`HeaderRooftopSelect error:`, errorText);
  }, [errorText]);

  const handleRooftopChange = (rooftop?: Customers) => {
    setRooftopId(rooftop?.id || 0);
    applyRooftopSwitch(rooftop);
  };

  if (hasAdminPrivileges && !isLoading)
    return (
      <Tooltip title='Select one of your markets'>
        <Autocomplete
          data-testid='header-rooftop-select'
          className={cls.rooftopAutocomplete}
          fullWidth
          disableClearable
          loading={false}
          loadingText={`Loading markets...`}
          noOptionsText={`No markets found`}
          options={rooftops}
          getOptionKey={r => r?.id || 0}
          getOptionLabel={r => r?.name || ``}
          value={rooftops?.find(r => r?.id === (currentRooftop?.id || rooftopId)) || undefined}
          onChange={(_event, value) => handleRooftopChange(value)}
          renderInput={params => (
            <TextField
              {...params}
              fullWidth
              name={`header-rooftop-select`}
              className={cls.rooftopTextField}
              placeholder='Select market...'
              // helperText={errorText}
              slotProps={{
                input: {
                  ...params?.InputProps,
                  startAdornment: (
                    <InputAdornment position='start'>
                      <BiStore className={cls.rooftopIcon} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Tooltip>
    );
  return null;
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => {
  const styles = {
    rooftopAutocomplete: css`
      min-width: 180px;
      max-width: 360px;
      ${theme?.breakpoints?.down('md')} {
        max-width: 100%;
      }
    `,
    rooftopTextField: css`
      & .MuiOutlinedInput-root {
        & fieldset {
          border-color: transparent;
          background-color: ${theme?.palette?.action?.selected};
          color: ${theme?.palette?.text?.primary};
          transition: all 0.1s ease-in-out;
        }
        &:hover fieldset {
          border-color: transparent;
          background-color: ${theme?.palette?.action?.focus};
        }
        &.Mui-focused fieldset {
          border-color: transparent;
        }
      }
    `,
    rooftopIcon: css`
      margin-left: 8px;
      margin-right: -6px;
      font-size: 18px;
      color: ${theme?.palette?.action?.active};
    `,
  };

  return styles;
};

// EXPORT ---------------------------------------------------------------- //

export default HeaderRooftopSelect;
